import { useCallback, useEffect, useState } from 'react';

// Fetch-on-mount helper: collapses the repeated loading/error/data boilerplate
// the AAI read tabs share. `load` returns an axios response; `errorMsg` is shown
// on failure. Re-runs when `deps` change.
//
// `reload` re-runs the same fetch on demand. A panel that writes — completing a
// step, submitting a review — calls it so the list behind it stops showing the
// state from before the write. Without it the screen quietly disagrees with the
// server until the next navigation.
export function useAsync<T>(load: () => Promise<{ data: T }>, errorMsg: string, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    load()
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setError(errorMsg); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, loading, error, reload };
}
